import { useMemo, useState } from "react";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Avatar from "@/components/base/Avatar";
import Badge from "@/components/base/Badge";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { EmptyState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import TemporaryPasswordDialog, { type TemporaryAccount } from "@/components/feature/TemporaryPasswordDialog";
import { useAuth } from "@/contexts/AuthContext";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { deleteUser, issueTemporaryPassword, updateUser } from "@/services/userApi";
import CycleBadge from "./CycleBadge";
import UserFormModal from "./UserFormModal";
import type { AppUser, CycleScope, Role } from "@/types";
import { useUrlSearch } from "@/hooks/useUrlSearch";


interface UsersPanelProps {
  users: AppUser[];
  roles: Role[];
  onChanged: () => void;
}

const selectClass =
  "rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 cursor-pointer";

export default function UsersPanel({ users, roles, onChanged }: UsersPanelProps) {
  const { notify } = useToast();
  const [search, setSearch] = useUrlSearch();
  const [roleFilter, setRoleFilter] = useState("all");
  const [cycleFilter, setCycleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const pager = usePager();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AppUser | null>(null);
  const [toDelete, setToDelete] = useState<AppUser | null>(null);
  const [toReset, setToReset] = useState<AppUser | null>(null);
  /** Mot de passe provisoire à afficher une seule fois (création ou réinitialisation). */
  const [issued, setIssued] = useState<{ account: TemporaryAccount; title: string } | null>(null);
  const { user: me, can } = useAuth();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const matchSearch =
        !q ||
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q);
      const matchRole = roleFilter === "all" || u.roleId === roleFilter;
      const matchCycle = cycleFilter === "all" || u.cycleScope === cycleFilter;
      const matchStatus =
        statusFilter === "all" || (statusFilter === "actif" ? u.isActive : !u.isActive);
      return matchSearch && matchRole && matchCycle && matchStatus;
    });
  }, [users, search, roleFilter, cycleFilter, statusFilter]);

  const page = pager.slice(filtered, `${search}|${roleFilter}|${cycleFilter}|${statusFilter}`);
  const rows = page.items;

  const resetFilters = () => {
    setSearch("");
    setRoleFilter("all");
    setCycleFilter("all");
    setStatusFilter("all");
  };

  const toggleActive = async (u: AppUser) => {
    try {
      await updateUser(u.id, { isActive: !u.isActive });
      onChanged();
      notify(
        u.isActive
          ? `Compte de ${u.firstName} ${u.lastName} désactivé.`
          : `Compte de ${u.firstName} ${u.lastName} activé.`,
        "info",
      );
    } catch (err) {
      notify(err instanceof Error ? err.message : "Opération impossible.", "error");
    }
  };

  const resetPassword = async () => {
    if (!toReset) return;
    try {
      const { temporaryPassword, emailSent } = await issueTemporaryPassword(toReset.id);
      setIssued({
        title: "Mot de passe réinitialisé",
        account: { email: toReset.email, firstName: toReset.firstName, lastName: toReset.lastName, temporaryPassword, emailSent },
      });
    } catch (err) {
      notify(err instanceof Error ? err.message : "Réinitialisation impossible.", "error");
    } finally {
      setToReset(null);
    }
  };

  return (
    <>
      <Card padded={false} className="animate-slide-up">
        <div className="flex flex-col gap-3 border-b border-background-200 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
              <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un nom, un email..."
                className="w-48 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className={selectClass}>
              <option value="all">Tous les rôles</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <select value={cycleFilter} onChange={(e) => setCycleFilter(e.target.value)} className={selectClass}>
              <option value="all">Tous les cycles</option>
              <option value="PRIMAIRE">Primaire</option>
              <option value="SECONDAIRE">Secondaire</option>
              <option value="GLOBAL">Global</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass}>
              <option value="all">Tous les statuts</option>
              <option value="actif">Actif</option>
              <option value="inactif">Inactif</option>
            </select>
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 text-xs font-medium text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i> Réinitialiser
            </button>
          </div>
          {can("users:create") && (
            <Button
              icon="ri-user-add-line"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              Ajouter un utilisateur
            </Button>
          )}
        </div>

        {rows.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon="ri-user-search-line"
              title="Aucun utilisateur trouvé"
              message="Ajustez vos filtres ou créez un nouvel utilisateur."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3 font-medium">Nom complet</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Rôle</th>
                  <th className="px-4 py-3 font-medium">Cycle</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-background-200 last:border-0 transition-colors hover:bg-background-100/60"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar lastName={u.lastName} firstName={u.firstName} src={u.avatar} size="sm" />
                        <div>
                          <p className="font-medium text-foreground-900">
                            {u.firstName} {u.lastName}
                          </p>
                          <p className="text-xs text-foreground-500">{u.phone ?? "—"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-700">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-foreground-800">{u.roleName}</span>
                    </td>
                    <td className="px-4 py-3">
                      <CycleBadge scope={u.cycleScope as CycleScope} />
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={u.isActive ? "success" : "neutral"} icon={u.isActive ? "ri-checkbox-circle-line" : "ri-forbid-line"}>
                        {u.isActive ? "Actif" : "Inactif"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {u.id === me?.id ? (
                          <span className="px-2 text-xs text-foreground-400">Vous (voir Mon compte)</span>
                        ) : (
                        <>
                        {can("users:update") && (
                        <>
                        <button
                          onClick={() => {
                            setEditing(u);
                            setFormOpen(true);
                          }}
                          className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 hover:text-foreground-800 transition-colors cursor-pointer"
                          aria-label="Modifier l'utilisateur"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => toggleActive(u)}
                          className={`press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                            u.isActive
                              ? "text-foreground-500 hover:bg-accent-50 hover:text-accent-600"
                              : "text-foreground-500 hover:bg-primary-50 hover:text-primary-700"
                          }`}
                          aria-label={u.isActive ? "Désactiver" : "Activer"}
                          title={u.isActive ? "Désactiver" : "Activer"}
                        >
                          <i className={u.isActive ? "ri-user-forbid-line" : "ri-user-follow-line"}></i>
                        </button>
                        <button
                          onClick={() => setToReset(u)}
                          className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 hover:text-foreground-800 transition-colors cursor-pointer"
                          aria-label="Réinitialiser le mot de passe"
                          title="Réinitialiser le mot de passe"
                        >
                          <i className="ri-lock-password-line"></i>
                        </button>
                        </>
                        )}
                        {can("users:delete") && (
                        <button
                          onClick={() => setToDelete(u)}
                          className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                          aria-label="Supprimer l'utilisateur"
                        >
                          <i className="ri-delete-bin-6-line"></i>
                        </button>
                        )}
                        </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination pager={page} className="border-t border-background-200" />
      </Card>

      <UserFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        user={editing}
        roles={roles}
        onSaved={onChanged}
        onCreated={(account) => setIssued({ account, title: "Compte créé" })}
      />

      <ConfirmDialog
        open={toReset !== null}
        title="Réinitialiser le mot de passe"
        message={`Un nouveau mot de passe provisoire sera généré pour ${toReset?.firstName} ${toReset?.lastName}. Sa session en cours sera fermée et il devra choisir un nouveau mot de passe à la prochaine connexion.`}
        confirmLabel="Générer un mot de passe"
        onClose={() => setToReset(null)}
        onConfirm={resetPassword}
      />

      <TemporaryPasswordDialog
        admin={issued?.account ?? null}
        schoolName={issued ? `${issued.account.firstName} ${issued.account.lastName}` : ""}
        title={issued?.title}
        onClose={() => setIssued(null)}
      />

      <ConfirmDialog
        open={toDelete !== null}
        title="Supprimer l'utilisateur"
        message={`Supprimer définitivement le compte de ${toDelete?.firstName} ${toDelete?.lastName} ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (toDelete) {
            try {
              await deleteUser(toDelete.id);
              onChanged();
              notify("Utilisateur supprimé.", "info");
            } catch (err) {
              notify(err instanceof Error ? err.message : "Suppression impossible.", "error");
            }
          }
        }}
      />
    </>
  );
}